import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useEffectiveRole } from '@/stores/authStore';
import { useAuth } from '@clerk/clerk-react';
import {
  AlertCircle,
  AlertTriangle,
  Bell,
  Info,
  Loader2,
  Megaphone,
  Plus,
  Search,
  Trash2,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

const API_URL = import.meta.env.VITE_API_URL || 'https://erg-shoot.des-466.workers.dev/api';

interface Announcement {
  id: string;
  title: string;
  body: string;
  category: string;
  priority: string;
  published_at: string;
  created_at: string;
}

interface AnnouncementsResponse {
  success: boolean;
  data: {
    announcements: Announcement[];
    categories: Record<string, number>;
    priorities: Record<string, number>;
    pagination: {
      has_more: boolean;
      next_cursor: string | null;
    };
    total_count: number;
    is_empty?: boolean;
    message?: string;
  };
}

// Category options
const categoryOptions = [
  { value: 'all', label: 'All Categories' },
  { value: 'general', label: 'General' },
  { value: 'training', label: 'Training' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'technical', label: 'Technical' },
  { value: 'billing', label: 'Billing' },
];

// Priority options
const priorityOptions = [
  { value: 'all', label: 'All Priorities' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

// Priority badge styles
const priorityStyles: Record<string, string> = {
  urgent: 'bg-red-500 text-white',
  high: 'bg-orange-500 text-white',
  medium: 'bg-yellow-500 text-black',
  low: 'bg-gray-500 text-white',
};

// Priority icons
const PriorityIcon = ({ priority }: { priority: string }) => {
  switch (priority) {
    case 'urgent':
      return <AlertCircle className="h-4 w-4" />;
    case 'high':
      return <AlertTriangle className="h-4 w-4" />;
    case 'medium':
      return <Bell className="h-4 w-4" />;
    default:
      return <Info className="h-4 w-4" />;
  }
};

// Category badge styles
const categoryStyles: Record<string, string> = {
  general: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  training: 'bg-green-500/10 text-green-500 border-green-500/20',
  marketing: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
  technical: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
  billing: 'bg-pink-500/10 text-pink-500 border-pink-500/20',
};

// Target role options for announcements
const targetRoleOptions = [
  { value: 'hq_admin', label: 'HQ Admins' },
  { value: 'agent', label: 'Agents' },
  { value: 'affiliate_owner', label: 'Affiliate Owners' },
  { value: 'affiliate_coach', label: 'Affiliate Coaches' },
  { value: 'member', label: 'Members' },
  { value: 'individual', label: 'Individuals' },
];

const ALL_ROLES = targetRoleOptions.map((r) => r.value);

export default function News() {
  const { getToken } = useAuth();
  const effectiveRole = useEffectiveRole();
  const isAdmin = effectiveRole === 'hq_admin';
  const isAgent = effectiveRole === 'agent';
  const isOwner = effectiveRole === 'affiliate_owner';
  const canCreateAnnouncement = isAdmin;

  // Filter visible target role options based on creator's role
  const visibleTargetRoleOptions = isAdmin
    ? targetRoleOptions
    : isAgent
      ? targetRoleOptions.filter((r) => ['affiliate_owner', 'affiliate_coach'].includes(r.value))
      : isOwner
        ? targetRoleOptions.filter((r) =>
            ['affiliate_owner', 'affiliate_coach', 'member'].includes(r.value)
          )
        : [];

  // Announcements state
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedPriority, setSelectedPriority] = useState('all');

  // Create modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newBody, setNewBody] = useState('');
  const [newCategory, setNewCategory] = useState('general');
  const [newPriority, setNewPriority] = useState('medium');
  const [targetRoles, setTargetRoles] = useState<string[]>(ALL_ROLES);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Fetch announcements from API
  const fetchAnnouncements = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const params = new URLSearchParams();
      if (selectedCategory !== 'all') {
        params.append('category', selectedCategory);
      }
      if (selectedPriority !== 'all') {
        params.append('priority', selectedPriority);
      }
      if (searchQuery) {
        params.append('search', searchQuery);
      }
      const url = `${API_URL}/announcements${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch announcements');
      }

      const data: AnnouncementsResponse = await response.json();
      if (data.success) {
        setAnnouncements(data.data.announcements);
      }
    } catch (err) {
      console.error('Failed to fetch announcements:', err);
      setError('Failed to load announcements. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [getToken, selectedCategory, selectedPriority, searchQuery]);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  const handleCreate = async () => {
    if (!newTitle || !newBody || targetRoles.length === 0) return;

    setIsCreating(true);
    setCreateError(null);
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/announcements`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: newTitle,
          body: newBody,
          category: newCategory,
          priority: newPriority,
          target_roles: targetRoles,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create announcement');
      }

      // Reset form and close modal
      setNewTitle('');
      setNewBody('');
      setNewCategory('general');
      setNewPriority('medium');
      setTargetRoles(ALL_ROLES);
      setShowCreateModal(false);

      // Refresh announcements list
      fetchAnnouncements();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create announcement';
      setCreateError(errorMessage);
      console.error('Create failed:', err);
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async (announcement: Announcement) => {
    if (!confirm(`Are you sure you want to delete "${announcement.title}"?`)) return;

    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/announcements/${announcement.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Delete failed');
      }

      // Refresh announcements list
      fetchAnnouncements();
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">NEWS & UPDATES</h1>
          <p className="text-muted-foreground">
            Stay informed with the latest ErgShoot announcements and updates.
          </p>
        </div>
        {canCreateAnnouncement && (
          <Button
            onClick={() => {
              setCreateError(null);
              setShowCreateModal(true);
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            New Announcement
          </Button>
        )}
      </div>

      {/* Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search announcements..."
            className="pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Select
          options={categoryOptions}
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="w-full sm:w-[180px]"
        />
        <Select
          options={priorityOptions}
          value={selectedPriority}
          onChange={(e) => setSelectedPriority(e.target.value)}
          className="w-full sm:w-[160px]"
        />
      </div>

      {/* Loading State */}
      {isLoading && (
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <span className="ml-2 text-muted-foreground">Loading announcements...</span>
          </CardContent>
        </Card>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <Card>
          <CardContent className="text-center py-12">
            <AlertCircle className="h-12 w-12 mx-auto text-destructive mb-4" />
            <p className="text-destructive">{error}</p>
            <Button variant="outline" className="mt-4" onClick={fetchAnnouncements}>
              Try Again
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {!isLoading && !error && announcements.length === 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Announcements</CardTitle>
            <CardDescription>Latest news and updates from ErgShoot HQ</CardDescription>
          </CardHeader>
          <CardContent className="text-center py-12">
            <Megaphone className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No Announcements Found</p>
            <p className="text-sm text-muted-foreground mt-2">
              {searchQuery || selectedCategory !== 'all' || selectedPriority !== 'all'
                ? 'Try adjusting your filters.'
                : isAdmin
                  ? 'Click "New Announcement" to create the first one.'
                  : 'No announcements have been posted yet.'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Announcements List */}
      {!isLoading && !error && announcements.length > 0 && (
        <div className="space-y-4">
          {announcements.map((announcement) => (
            <Card
              key={announcement.id}
              className={`group hover:border-primary/50 transition-colors ${
                announcement.priority === 'urgent' ? 'border-red-500/50' : ''
              } ${announcement.priority === 'high' ? 'border-orange-500/30' : ''}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  {/* Priority indicator */}
                  <div
                    className={`flex-shrink-0 p-2 rounded-lg ${priorityStyles[announcement.priority] || 'bg-gray-500 text-white'}`}
                  >
                    <PriorityIcon priority={announcement.priority} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="font-semibold text-lg">{announcement.title}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge
                            variant="outline"
                            className={categoryStyles[announcement.category] || ''}
                          >
                            {announcement.category}
                          </Badge>
                          <Badge
                            variant="outline"
                            className={priorityStyles[announcement.priority]}
                          >
                            {announcement.priority}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {formatDate(announcement.published_at)}
                          </span>
                        </div>
                      </div>

                      {canCreateAnnouncement && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => handleDelete(announcement)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>

                    <p className="mt-3 text-muted-foreground whitespace-pre-wrap">
                      {announcement.body}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>New Announcement</DialogTitle>
            <DialogDescription>
              Create a new announcement and choose which user roles can see it.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Create Error */}
            {createError && (
              <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {createError}
              </div>
            )}

            {/* Form Fields */}
            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="Announcement title"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="body">Content</Label>
                <Textarea
                  id="body"
                  value={newBody}
                  onChange={(e) => setNewBody(e.target.value)}
                  placeholder="Write your announcement..."
                  rows={5}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Select
                    options={categoryOptions.filter((c) => c.value !== 'all')}
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    className="w-full"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="priority">Priority</Label>
                  <Select
                    options={priorityOptions.filter((p) => p.value !== 'all')}
                    value={newPriority}
                    onChange={(e) => setNewPriority(e.target.value)}
                    className="w-full"
                  />
                </div>
              </div>

              {/* Target Roles */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Visible To</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-auto py-1 px-2 text-xs"
                    onClick={() => {
                      const allVisible = visibleTargetRoleOptions.map((r) => r.value);
                      if (targetRoles.length === allVisible.length) {
                        setTargetRoles([]);
                      } else {
                        setTargetRoles(allVisible);
                      }
                    }}
                  >
                    {targetRoles.length === visibleTargetRoleOptions.length
                      ? 'Deselect All'
                      : 'Select All'}
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {visibleTargetRoleOptions.map((role) => (
                    <div key={role.value} className="flex items-center space-x-2">
                      <Checkbox
                        id={`role-${role.value}`}
                        checked={targetRoles.includes(role.value)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setTargetRoles((prev) => [...prev, role.value]);
                          } else {
                            setTargetRoles((prev) => prev.filter((r) => r !== role.value));
                          }
                        }}
                      />
                      <Label
                        htmlFor={`role-${role.value}`}
                        className="text-sm font-normal cursor-pointer"
                      >
                        {role.label}
                      </Label>
                    </div>
                  ))}
                </div>
                {targetRoles.length === 0 && (
                  <p className="text-xs text-destructive">
                    Select at least one role to see this announcement.
                  </p>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                disabled={!newTitle || !newBody || targetRoles.length === 0 || isCreating}
              >
                {isCreating ? 'Creating...' : 'Create Announcement'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
